import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, UserCheck, UserX, KeyRound, Copy, Check, Eye, EyeOff, ShieldAlert, FileDown, LogIn, ArrowRightLeft, Search, Activity } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLES, REGIONS } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const GLOBAL_ROLES = ["super_admin", "management"];
const NONE = "__none__";
const INTERNAL_SALES = ["sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson", "bd", "sc"];
const TRANSFER_EMAILS = ["nataraju.s@united-group.in", "shabbir@united-group.in"];

function TransferBookDialog({ source, users, onOpenChange, onDone }) {
  const [target, setTarget] = useState("");
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!source) { setTarget(""); setCounts(null); return; }
    api.get(`/users/${source.id}/record-counts`).then((r) => setCounts(r.data)).catch(() => setCounts(null));
  }, [source]);

  const options = users.filter((u) => u.is_active && INTERNAL_SALES.includes(u.role) && u.id !== source?.id);

  const transfer = async () => {
    if (!target) { toast.error("Pick who receives the records"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${source.id}/transfer-records`, { target_user_id: target });
      toast.success(`Done — ${data.leads_moved} leads & ${data.opps_moved} opportunities moved from ${data.source_name} to ${data.target_name}`);
      onOpenChange(null);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!source} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent className="max-w-md" data-testid="transfer-book-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-brand" /> Transfer {source?.name}'s entire book</DialogTitle>
          <DialogDescription>
            Moves <strong>{counts ? `${counts.leads} leads and ${counts.opportunities} opportunities` : "all leads and opportunities"}</strong> owned by {source?.name} to the member you pick. The new owner is notified and every record's timeline logs the handover.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-700">Transfer everything to</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="h-9" data-testid="transfer-target"><SelectValue placeholder="Select new owner" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {options.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.region ? ` · ${u.region}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          This is a one-click bulk action — it can be undone only by transferring back.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(null)} data-testid="transfer-cancel">Cancel</Button>
          <Button onClick={transfer} disabled={busy || !target} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="transfer-confirm">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Transfer Everything
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TempPasswordDialog({ data, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(data?.temp_password || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-4 w-4 text-brand" /> Temporary password</DialogTitle>
          <DialogDescription>Share this with <span className="font-medium text-slate-700">{data?.email}</span>. They'll be forced to set a new password on first sign-in.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
          <code className="text-base font-mono font-semibold text-slate-900 flex-1" data-testid="temp-password-value">{data?.temp_password}</code>
          <Button variant="outline" size="sm" onClick={copy} className="gap-1.5" data-testid="copy-temp-password">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <p className="text-xs text-slate-400">This password is shown only once — copy it now.</p>
        <DialogFooter>
          <Button onClick={() => onOpenChange(null)} className="bg-slate-900 hover:bg-slate-800" data-testid="temp-password-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordRow({ u }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(u.password || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <TableRow data-testid={`pw-row-${u.id}`}>
      <TableCell className="font-medium">{u.name}</TableCell>
      <TableCell className="text-slate-600 text-sm">{u.email}</TableCell>
      <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">{ROLE_LABELS[u.role] || u.role}</span></TableCell>
      <TableCell>
        {u.captured ? (
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm text-slate-900 min-w-[90px]" data-testid={`pw-value-${u.id}`}>{show ? u.password : "••••••••"}</code>
            <button onClick={() => setShow((s) => !s)} className="text-slate-400 hover:text-slate-700" data-testid={`pw-toggle-${u.id}`} title={show ? "Hide" : "Show"}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            <button onClick={copy} className="text-slate-400 hover:text-slate-700" title="Copy">{copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}</button>
            {u.on_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">default</span>}
          </div>
        ) : (
          <span className="text-xs text-slate-400 italic">not captured yet — will show after next change</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function PasswordsDialog({ open, onOpenChange }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["user-passwords"],
    queryFn: async () => (await api.get("/users/passwords")).data,
    enabled: open,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="passwords-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-4 w-4 text-brand" /> User Passwords</DialogTitle>
          <DialogDescription>Super-admin only. Passwords set before this feature was added can't be recovered and show as "not captured".</DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-100 px-3 py-2 text-[11px] text-red-700" data-testid="passwords-warning">
          <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" /> Sensitive. Storing/showing plaintext passwords is a security risk — keep this screen private and avoid sharing.
        </div>
        {isLoading ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Name", "Email", "Role", "Password"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>{data.map((u) => <PasswordRow key={u.id} u={u} />)}</TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ open, onOpenChange, initial, managers, onSaved, onTemp }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) setF(initial || { role: "salesperson" }); }, [open, initial]);
  const needsDivision = !GLOBAL_ROLES.includes(f.role);

  const save = async () => {
    if (!f.name || !f.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const region = f.region && f.region !== NONE ? f.region : null;
      const manager_id = f.manager_id && f.manager_id !== NONE ? f.manager_id : null;
      if (initial?.id) {
        const body = { name: f.name, phone: f.phone, role: f.role, region, manager_id,
          role_tag: f.role_tag || null, division_id: needsDivision ? "univicoustic" : null };
        if (f.password) body.password = f.password;
        await api.put(`/users/${initial.id}`, body);
        toast.success("User updated");
      } else {
        const { data } = await api.post("/users", {
          name: f.name, email: f.email, password: f.password || undefined, phone: f.phone || "",
          role: f.role, region, manager_id, role_tag: f.role_tag || null,
          division_id: needsDivision ? "univicoustic" : null,
        });
        toast.success("User created");
        if (data.temp_password) onTemp({ email: data.email, temp_password: data.temp_password });
      }
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit User" : "Create User"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Name *</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="user-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email *</Label><Input type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} disabled={!!initial?.id} className="h-9" data-testid="user-email" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className="h-9" data-testid="user-phone" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Role *</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="h-9" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Region</Label>
            <Select value={f.region || NONE} onValueChange={(v) => set("region", v)}>
              <SelectTrigger className="h-9" data-testid="user-region"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— None —</SelectItem>
                {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Reports to</Label>
            <Select value={f.manager_id || NONE} onValueChange={(v) => set("manager_id", v)}>
              <SelectTrigger className="h-9" data-testid="user-manager"><SelectValue placeholder="Manager" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={NONE}>— None —</SelectItem>
                {managers.filter((m) => m.id !== initial?.id).map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name} · {ROLE_LABELS[m.role]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Tag (BD / SC)</Label><Input value={f.role_tag || ""} onChange={(e) => set("role_tag", e.target.value)} className="h-9" placeholder="Optional" data-testid="user-roletag" /></div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">{initial?.id ? "Set Password (optional)" : "Password (optional)"}</Label>
            <Input type="text" value={f.password || ""} onChange={(e) => set("password", e.target.value)} className="h-9"
              placeholder={initial?.id ? "Leave blank to keep current" : "Leave blank to auto-generate a temporary password"} data-testid="user-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="user-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user, impersonate } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [temp, setTemp] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [transferSource, setTransferSource] = useState(null);
  const isSuperAdmin = user?.role === "super_admin";
  const canTransfer = isSuperAdmin || TRANSFER_EMAILS.includes((user?.email || "").toLowerCase());

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const nameById = Object.fromEntries(users.map((u) => [u.id, u.name]));
  const [q, setQ] = useState("");
  const shown = users.filter((u) => !q.trim() ||
    `${u.name} ${u.email} ${ROLE_LABELS[u.role] || u.role} ${u.region || ""} ${nameById[u.manager_id] || ""}`.toLowerCase().includes(q.trim().toLowerCase()));

  const toggleHidden = async (u) => {
    await api.put(`/users/${u.id}`, { hidden_from_team: !u.hidden_from_team });
    qc.invalidateQueries({ queryKey: ["users"] });
    toast.success(u.hidden_from_team ? `${u.name} is visible in team views again` : `${u.name} hidden from team views (Admin only)`);
  };
  const toggleActive = async (u) => {
    await api.post(`/users/${u.id}/${u.is_active ? "deactivate" : "activate"}`);
    qc.invalidateQueries({ queryKey: ["users"] });
    toast.success(u.is_active ? "User deactivated" : "User activated");
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTemp({ email: data.email, temp_password: data.temp_password });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewAs = async (u) => {
    const r = await impersonate(u.id);
    if (!r.ok) toast.error(r.error);
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 4000);
      toast.success(`${filename} downloaded`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Users & Hierarchy" subtitle="Create accounts, set region & reporting lines. Temporary passwords force a reset on first sign-in.">
        {["super_admin", "sales_head", "management"].includes(user?.role) && (
          <Button variant="outline" className="gap-1.5" onClick={() => (window.location.href = "/team-activity")} data-testid="goto-team-activity-btn">
            <Activity className="h-4 w-4" /> Team Activity
          </Button>
        )}
        {isSuperAdmin && (
          <>
            <Button variant="outline" className="gap-1.5" onClick={() => download("/users/export/sales-team", "univicoustic_crm_salesteam_logins.xlsx")} data-testid="export-salesteam-btn"><FileDown className="h-4 w-4" /> Sales team logins</Button>
            <Button variant="outline" className="gap-1.5" onClick={() => download("/users/export/dealers", "univicoustic_dealer_logins.xlsx")} data-testid="export-dealers-btn"><FileDown className="h-4 w-4" /> Dealer logins</Button>
            <Button variant="outline" className="gap-1.5" onClick={() => download("/users/export/who-fills-what", "univicoustic_crm_who_fills_what.xlsx")} data-testid="export-guide-btn"><FileDown className="h-4 w-4" /> Who fills what</Button>
            <Button variant="outline" className="gap-1.5" onClick={() => download("/users/export/credentials", "univicoustic_crm_usernames_passwords.xlsx")} data-testid="export-credentials-btn"><FileDown className="h-4 w-4" /> All passwords</Button>
            <Button variant="outline" className="gap-1.5" onClick={() => setPwOpen(true)} data-testid="view-passwords-btn"><KeyRound className="h-4 w-4" /> View passwords</Button>
          </>
        )}
        {isSuperAdmin && (
          <Button className="bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-user-btn"><Plus className="h-4 w-4" /> Create User</Button>
        )}
      </PageHeader>

      <div className="relative mb-3 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, role, region or manager…"
          className="pl-8 h-9 bg-white" data-testid="users-search-input" />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Name", "Email", "Role", "Region", "Reports to", "Status", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {shown.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400" data-testid="users-search-empty">No users match “{q}”.</TableCell></TableRow>
                )}
                {shown.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}{u.role_tag ? <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold">{u.role_tag}</span> : null}</TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">{ROLE_LABELS[u.role] || u.role}</span></TableCell>
                    <TableCell>{u.region ? <span className="text-xs font-medium text-slate-700">{u.region}</span> : <span className="text-slate-300 text-sm">—</span>}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{nameById[u.manager_id] || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell>
                      {u.is_active ? <span className="text-xs text-emerald-600 font-medium">Active</span> : <span className="text-xs text-slate-400 font-medium">Inactive</span>}
                      {u.must_change_password && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">temp pw</span>}
                      {u.hidden_from_team && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold" data-testid={`hidden-badge-${u.id}`}>admin only</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        {u.is_active && u.role !== "super_admin" && (
                          <Button variant="ghost" size="sm" className="text-indigo-600" title={`View the app as ${u.name}`} onClick={() => viewAs(u)} data-testid={`impersonate-user-${u.id}`}><LogIn className="h-4 w-4" /></Button>
                        )}
                        {canTransfer && INTERNAL_SALES.includes(u.role) && (
                          <Button variant="ghost" size="sm" className="text-amber-600" title={`Transfer ALL of ${u.name}'s leads & opportunities`} onClick={() => setTransferSource(u)} data-testid={`transfer-user-${u.id}`}><ArrowRightLeft className="h-4 w-4" /></Button>
                        )}
                        {isSuperAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-user-${u.id}`}>Edit</Button>
                            <Button variant="ghost" size="sm" className="text-slate-500" title="Reset password" onClick={() => resetPassword(u)} data-testid={`reset-user-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                            {u.role !== "super_admin" && (
                              <Button variant="ghost" size="sm" className={u.hidden_from_team ? "text-slate-400" : "text-slate-500"} title={u.hidden_from_team ? "Hidden from team views — click to show" : "Hide from team views (visible to Admin only)"} onClick={() => toggleHidden(u)} data-testid={`hide-user-${u.id}`}>
                                {u.hidden_from_team ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className={u.is_active ? "text-red-600" : "text-emerald-600"} onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>
                              {u.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      <UserDialog open={open} onOpenChange={setOpen} initial={editing} managers={users}
        onSaved={() => qc.invalidateQueries({ queryKey: ["users"] })} onTemp={setTemp} />
      <TempPasswordDialog data={temp} onOpenChange={setTemp} />
      <PasswordsDialog open={pwOpen} onOpenChange={setPwOpen} />
      <TransferBookDialog source={transferSource} users={users} onOpenChange={setTransferSource}
        onDone={() => qc.invalidateQueries({ queryKey: ["users"] })} />
    </div>
  );
}
